import React, { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tab, TabPanel, Tabs, TabList, ListItemMarket, PlainTooltip } from "@similarweb/ui-components";

import { showSuccessToast } from "app/actions/toast_actions";
import { SharingAssetModal } from "app/components/sharingAssetModal/SharingAssetModal";
import { i18nFilter } from "app/filters/i18nFilter";
import { getModalText } from "app/pages/segments/analysis/sharingModalCopy";
import type {
    ICustomSegment,
    ICustomSegmentAccount,
    ICustomSegmentsGroup,
} from "app/services/segments/segmentsApiService";
import SegmentsApiService from "app/services/segments/segmentsApiService";
import type { ICustomSegmentMember } from "app/services/segments/SegmentsUtils";
import { getHasSegmentShareClaim, SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { ListItemSegment } from "pro-features/pages/segments/components/ListItems";

import { MODE } from "../analysis/SegmentsAnalysisTrafficContainerMode";

import { ListItemCollapsibleSeparator, ScrollAreaWrapper } from "./AutocompleteDropdownTabs";
import { DropdownEmptyContentContainer, QueryBarAutocomplete, QueryBarAutocompleteContainer } from "./styledComponents";

const i18n = i18nFilter();

const { orange } = colorsPalettes;

const AUTOCOMPLETE_DEBOUNCE = 150;
const AUTOCOMPLETE_MAX_LIST_SIZE = 300;

const getSegmentsDropdownSections = () => [
    {
        key: "mySegments",
        title: i18n("segments.dropdown.sections.my.segments"),
    },
    {
        key: "sharedWithMeSegments",
        title: i18n("segments.dropdown.sections.shared.with.me.segments"),
    },
    {
        key: "accountSegments",
        title: i18n("segments.dropdown.sections.account.segments"),
    },
];

export const DropdownEmptyContent = ({ message, iconName = "no-search-results" as IconName }) => (
    <DropdownEmptyContentContainer>
        <SWReactIcons
            iconName={iconName}
            size="sm"
        />
        <span>{message}</span>
    </DropdownEmptyContentContainer>
);

const SegmentsDropdownContentComponent = ({ segments, selectItem, onCloseAutoComplete }) => {
    const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
    const [segmentForShare, setSegmentForShare] = useState<ICustomSegment>(null);
    const dispatch = useDispatch();
    const segmentsApiService = new SegmentsApiService();
    const isSegmentSharingEnabled = getHasSegmentShareClaim();

    const onShareSubmit = React.useCallback(
        async (sharing) => {
            if (!segmentForShare) {
                return;
            }
            await segmentsApiService.shareSegment(segmentForShare, sharing);
            dispatch(showSuccessToast(i18n("segment.sharing.success.toast")));
            setIsShareModalOpen(false);
            onCloseAutoComplete?.();
        },
        [dispatch, onCloseAutoComplete, segmentForShare, segmentsApiService],
    );

    const handleChangeModalState = (item: ICustomSegment, isOpen: boolean) => {
        setIsShareModalOpen(isOpen);
        setSegmentForShare(item);
    };
    const segmentsDropdownSections = React.useMemo(() => getSegmentsDropdownSections(), []);
    const hasResults = React.useMemo(
        () => segmentsDropdownSections.some((sect) => segments[sect.key]?.length > 0),
        [segments, segmentsDropdownSections],
    );

    return hasResults ? (
        <>
            {segmentsDropdownSections.map((sect) =>
                segments[sect.key]?.length > 0 ? (
                    <>
                        <ListItemCollapsibleSeparator
                            key={sect.key}
                            title={sect.title}
                        >
                            {segments[sect.key].slice(0, AUTOCOMPLETE_MAX_LIST_SIZE).map((item: ICustomSegment) => {
                                const { id, isBlacklisted, domain, segmentName, favicon } = item;
                                return (
                                    <PlainTooltip
                                        tooltipContent={i18n(
                                            "segment.analysis.org.compare.modal.column.disabled.tooltip",
                                        )}
                                        placement={"top"}
                                        enabled={item.isBlacklisted}
                                        key={id}
                                    >
                                        <ListItemSegment
                                            key={id}
                                            isMyDomain={segmentsDropdownSections[0].key === sect.key}
                                            hasSegmentShareClaim={isSegmentSharingEnabled}
                                            item={item}
                                            domain={domain}
                                            name={segmentName}
                                            img={favicon}
                                            onClick={() => (!isBlacklisted ? selectItem(item) : null)}
                                            className={`${isBlacklisted ? "list-item-segment--disabled" : ""}`}
                                            onActionClick={handleChangeModalState}
                                        />
                                    </PlainTooltip>
                                );
                            })}
                        </ListItemCollapsibleSeparator>
                        {segmentForShare ? (
                            <SharingAssetModal
                                onSubmit={onShareSubmit}
                                modalText={getModalText(segmentForShare)}
                                sharedWithUsers={segmentForShare.sharedWithUsers}
                                initialSharingState={segmentsApiService.resolveSharingStateToShareModalFormat(
                                    segmentForShare,
                                )}
                                isOpen={isShareModalOpen}
                                setIsOpen={setIsShareModalOpen}
                            />
                        ) : null}
                    </>
                ) : null,
            )}
        </>
    ) : (
        <DropdownEmptyContent message={i18n("segments.querybar.dropdown.segments.noResults")} />
    );
};
const SegmentsDropdownContent = React.memo(SegmentsDropdownContentComponent);

const GroupsDropdownContentComponent = ({ groups, selectItem }) =>
    groups.length > 0 ? (
        <>
            {groups.slice(0, AUTOCOMPLETE_MAX_LIST_SIZE).map((item) => (
                <ListItemMarket
                    key={item.id}
                    text={item.name}
                    iconName="segment-folder"
                    onClick={() => selectItem(item)}
                />
            ))}
        </>
    ) : (
        <DropdownEmptyContent message={i18n("segments.querybar.dropdown.groups.noResults")} />
    );
const GroupsDropdownContent = React.memo(GroupsDropdownContentComponent);

const DropdownItemsContent: React.FC<{
    listItems: {
        _PROPS_: any;
        segments: { [key: string]: ICustomSegmentMember[] };
        groups: { [key: string]: ICustomSegmentsGroup[] };
    };
    onItemSelect;
}> = ({ listItems, onItemSelect }) => {
    const { state, params } = useCurrentStateAndParams();
    const { _PROPS_: listProps = undefined, segments = {}, groups = {} } = listItems;
    const { query, selectEntity, defaultTabIndex = 0, closeFn } = listProps || {};

    const scrollWrapperRef = React.useRef(null);
    React.useEffect(() => {
        if (scrollWrapperRef.current) {
            scrollWrapperRef.current.scrollTop();
        }
    }, [query]);

    const [selectedTabIdx, setSelectedTabIdx] = React.useState(defaultTabIndex);
    React.useEffect(() => {
        setSelectedTabIdx(defaultTabIndex);
    }, [defaultTabIndex]);
    const { selectSegment, selectGroup } = React.useMemo(
        () => ({
            selectSegment: (segment, skipTrackEvent = false) =>
                onItemSelect(() => selectEntity(MODE.single, segment, skipTrackEvent)),
            selectGroup: (group) => onItemSelect(() => selectEntity(MODE.group, group)),
        }),
        [onItemSelect, selectEntity],
    );

    const [numQuerySegments, numQueryGroups] = React.useMemo(() => {
        const countSection = (sectionEntities) =>
            Object.entries<any[]>(sectionEntities).reduce((acc, [_, segSectItems]) => acc + segSectItems.length, 0);
        return [countSection(segments), countSection(groups)];
    }, [segments, groups]);
    const isMidTierUser = React.useMemo(() => SegmentsUtils.isMidTierUser(), []);

    return (
        <div className="ListItemsContainer">
            <Tabs
                selectedIndex={selectedTabIdx}
                onSelect={setSelectedTabIdx}
                className="ListItemsTabs"
            >
                <TabList>
                    <Tab>Segments ({numQuerySegments})</Tab>
                    <Tab disabled={isMidTierUser}>Comparisons ({numQueryGroups})</Tab>
                </TabList>
                <TabPanel>
                    <ScrollAreaWrapper ref={scrollWrapperRef}>
                        <SegmentsDropdownContent
                            segments={segments}
                            selectItem={selectSegment}
                            onCloseAutoComplete={closeFn}
                        />
                    </ScrollAreaWrapper>
                </TabPanel>
                <TabPanel>
                    <ScrollAreaWrapper ref={scrollWrapperRef}>
                        <GroupsDropdownContent
                            groups={groups.myGroups}
                            selectItem={selectGroup}
                        />
                    </ScrollAreaWrapper>
                </TabPanel>
            </Tabs>
        </div>
    );
};
const makeDropdownItemsContent = (props) => <DropdownItemsContent {...props} />;
interface ISegmentsQueryBarAutocompleteProps {
    mySegments: ICustomSegment[];
    accountSegments: ICustomSegmentAccount[];
    sharedWithMeSegments: ICustomSegmentAccount[];
    myGroups: ICustomSegmentsGroup[];
    navigateChangeEntity: (
        mode: string,
        entity: ICustomSegment | ICustomSegmentsGroup,
        skipTrackEvent?: boolean,
    ) => void;
    defaultTab?: string;
    closeFn: () => void;
}

export const SegmentsQueryBarAutocomplete = (props: ISegmentsQueryBarAutocompleteProps) => {
    const { mySegments, accountSegments, myGroups, sharedWithMeSegments, navigateChangeEntity, defaultTab, closeFn } =
        props;

    const preventKeyUp = (evt) => {
        // stop handling those keys events since they cause errors for custom autocomplete content
        switch (evt.key) {
            case "ArrowUp":
            case "ArrowDown":
            case "Enter":
            case "Escape":
                evt.stopPropagation();
                break;
        }
    };

    const selectEntity = React.useCallback(
        (mode, entity, skipTrackEvent) => {
            navigateChangeEntity(mode, entity, skipTrackEvent);
            closeFn();
        },
        [navigateChangeEntity, closeFn],
    );

    const getItemsStructured = React.useCallback(
        (query) => {
            const defaultTabIndex = { segments: 0, groups: 1 }[defaultTab];
            const items = {
                _PROPS_: { query, selectEntity, defaultTabIndex, closeFn },
                segments: {
                    mySegments: mySegments.filter((segment) => SegmentsUtils.matchSegments.byQuery(segment, query)),
                    accountSegments: accountSegments.filter((segment) =>
                        SegmentsUtils.matchSegments.byQuery(segment, query),
                    ),
                    sharedWithMeSegments: sharedWithMeSegments.filter((segment) =>
                        SegmentsUtils.matchSegments.byQuery(segment, query),
                    ),
                },
                groups: {
                    myGroups: myGroups.filter((group) => SegmentsUtils.matchSegmentsGroups.byQuery(group, query)),
                },
            };
            return items;
        },
        [mySegments, accountSegments, myGroups, sharedWithMeSegments],
    );

    return (
        <QueryBarAutocompleteContainer onKeyUpCapture={preventKeyUp}>
            <QueryBarAutocomplete
                className="QueryBarAutocomplete"
                floating={true}
                resetValueOnSelect={true}
                preventTruncateUnlessForced={true}
                getListItems={getItemsStructured}
                debounce={AUTOCOMPLETE_DEBOUNCE}
                placeholder={i18n("segments.querybar.autocomplete.placeholder")}
                renderItems={makeDropdownItemsContent}
                isFocused={true}
            />
        </QueryBarAutocompleteContainer>
    );
};
